import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { Article } from "../interfaces/Article";
import { getLiveArticleState } from "../services/core-api";
import {
  canFetchLiveArticleState,
  getArticleLiveQueryKey,
  isArticleAvailableForSale,
} from "../utils/articleAvailability";

const BATCH_SIZE = 8;
const INITIAL_SCAN_BATCHES = 2;

export function useProgressiveInStockFilter(articles: Article[], inStockOnly: boolean) {
  const queryClient = useQueryClient();
  const [availableArticles, setAvailableArticles] = useState<Article[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [hasScannedAll, setHasScannedAll] = useState(false);

  const scanIndexRef = useRef(0);
  const scanningLockRef = useRef(false);
  const articlesRef = useRef(articles);
  articlesRef.current = articles;

  const reset = useCallback(() => {
    scanIndexRef.current = 0;
    scanningLockRef.current = false;
    setAvailableArticles([]);
    setHasScannedAll(false);
    setIsScanning(false);
  }, []);

  useEffect(() => {
    if (inStockOnly) {
      reset();
    }
  }, [articles, inStockOnly, reset]);

  const scanNextBatch = useCallback(async () => {
    const currentArticles = articlesRef.current;

    if (
      !inStockOnly ||
      scanningLockRef.current ||
      scanIndexRef.current >= currentArticles.length
    ) {
      if (scanIndexRef.current >= currentArticles.length && inStockOnly) {
        setHasScannedAll(true);
      }
      return;
    }

    scanningLockRef.current = true;
    setIsScanning(true);

    const start = scanIndexRef.current;
    const batch = currentArticles.slice(start, start + BATCH_SIZE);
    scanIndexRef.current = start + batch.length;

    const availableInBatch: Article[] = [];

    await Promise.all(
      batch.map(async (article) => {
        if (!canFetchLiveArticleState(article)) {
          return;
        }

        try {
          const data = await queryClient.fetchQuery({
            queryKey: getArticleLiveQueryKey(article),
            queryFn: ({ signal }) =>
              getLiveArticleState(
                {
                  articleNumber: article.articleNumber.replace(/\//g, "*"),
                  dataSupplierId: article.dataSupplierId,
                  quantity: 1,
                },
                signal
              ),
            staleTime: Infinity,
            gcTime: 30 * 60 * 1000,
          });

          if (isArticleAvailableForSale(data)) {
            availableInBatch.push(article);
          }
        } catch {
          // unavailable or request failed
        }
      })
    );

    if (availableInBatch.length > 0) {
      setAvailableArticles((prev) => [...prev, ...availableInBatch]);
    }

    if (scanIndexRef.current >= currentArticles.length) {
      setHasScannedAll(true);
    }

    scanningLockRef.current = false;
    setIsScanning(false);
  }, [inStockOnly, queryClient]);

  useEffect(() => {
    if (!inStockOnly || articles.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      for (let i = 0; i < INITIAL_SCAN_BATCHES && !cancelled; i++) {
        if (scanIndexRef.current >= articles.length) {
          break;
        }
        await scanNextBatch();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [articles, inStockOnly, scanNextBatch]);

  const loadMore = useCallback(() => {
    if (!inStockOnly || hasScannedAll || isScanning) {
      return;
    }
    void scanNextBatch();
  }, [hasScannedAll, inStockOnly, isScanning, scanNextBatch]);

  const displayArticles = inStockOnly ? availableArticles : articles;

  return {
    displayArticles,
    isScanning: inStockOnly && isScanning,
    hasScannedAll: inStockOnly && hasScannedAll,
    loadMore,
  };
}
